import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, Send, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function BacklogButton() {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [idea, setIdea] = useState("");
  const [pagePath, setPagePath] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-populate page path when popover opens
  useEffect(() => {
    if (isOpen) {
      setPagePath(location.pathname);
    }
  }, [isOpen, location.pathname]);

  if (!user || !isAdmin) return null;

  const handleSubmit = async () => {
    if (!idea.trim()) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("admin_backlog")
        .insert({ 
          idea: idea.trim(), 
          created_by: user.id,
          page_path: pagePath.trim() || null 
        });

      if (error) throw error;

      toast({ title: "Idea added to backlog" });
      setIdea("");
      setPagePath("");
      setIsOpen(false);
    } catch (err) {
      console.error("Failed to add idea:", err);
      toast({ title: "Failed to add idea", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="secondary" 
          size="sm" 
          className="px-2.5 shadow-sm ring-1 ring-border" 
          aria-label="Add to backlog"
          data-track-cta
        >
          <MessageSquarePlus className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Add to Backlog</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Page path input */}
          <div className="space-y-1.5">
            <Label htmlFor="page-path" className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Page Context
            </Label>
            <Input
              id="page-path"
              placeholder="/path/to/page"
              value={pagePath}
              onChange={(e) => setPagePath(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          
          {/* Idea input */}
          <div className="space-y-1.5">
            <Label htmlFor="idea" className="text-xs text-muted-foreground">
              Idea
            </Label>
            <Textarea
              id="idea"
              placeholder="Quick idea or note..."
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              className="min-h-[80px] resize-none"
              autoFocus
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!idea.trim() || isSubmitting}
            className="w-full"
            size="sm"
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? "Adding..." : "Add Idea"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
